import type { AdController, Scte35Marker } from "../types";
import type { AdTimingService } from "./AdTimingService";

export interface AdPreloadPoolDeps {
  debug: boolean;
  createAdPlayer(continueLiveStreamDuringAds: boolean): AdController;
  shouldContinueLiveStreamDuringAds(): boolean;
  timing: AdTimingService;
  generateVastUrlsWithCorrelators(baseUrl: string, count: number): string[];
  isInAdBreak(): boolean;
  getPendingAdBreak(): { marker: Scte35Marker; vastUrls: string[]; isFetching: boolean } | null;
}

export class AdPreloadPool {
  private pool: Array<{
    vastUrl: string;
    adController: AdController;
    loadedAt: number;
    isReady: boolean;
    loadPromise: Promise<void>;
    durationSeconds?: number;
  }> = [];
  private readonly maxPoolSize: number = 2;
  private poolActive: boolean = false;
  private loopRunning: boolean = false;

  private deps: AdPreloadPoolDeps;

  constructor(deps: AdPreloadPoolDeps) {
    this.deps = deps;
  }

  get active(): boolean {
    return this.poolActive;
  }

  set active(value: boolean) {
    this.poolActive = value;
  }

  get isLoopRunning(): boolean {
    return this.loopRunning;
  }

  startPreloadPool(
    baseVastUrl: string,
    initialUrls: string[],
    startLoopImmediately: boolean = true
  ): void {
    if (this.poolActive) {
      if (this.deps.debug) {
        console.log("[PRELOAD-POOL] Already active, skipping duplicate start");
      }
      return;
    }

    this.poolActive = true;

    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Starting preload pool EARLY (target size: ${this.maxPoolSize})`);
      console.log(`[PRELOAD-POOL] Ad break hasn't started yet, but preloading ads in advance`);
    }

    for (const url of initialUrls) {
      if (this.pool.length < this.maxPoolSize) {
        this.preloadAdIntoPool(url);
      }
    }

    if (startLoopImmediately) {
      this.preloadPoolLoop(baseVastUrl);
    } else if (this.deps.debug) {
      console.log(
        "[PRELOAD-POOL] Pre-break mode: preloaded once, waiting for SCTE start to continue fetching"
      );
    }
  }

  private async preloadAdIntoPool(vastUrl: string): Promise<void> {
    const timing = this.deps.timing;

    if (timing.isGamInCooldown()) {
      if (this.deps.debug) {
        console.log("[CIRCUIT-BREAKER] GAM in cooldown, skipping preload");
      }
      return;
    }

    if (
      this.pool.some(entry => entry.vastUrl === vastUrl) ||
      timing.failedVastUrls.has(vastUrl) ||
      timing.isUrlInCooldown(vastUrl)
    ) {
      return;
    }

    if (this.pool.length >= this.maxPoolSize) {
      return;
    }

    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Preloading ad into pool: ${vastUrl}`);
    }

    const epoch = timing.adBreakEpoch;
    const loadPromise = (async () => {
      let hasAdError = false;
      let adErrorPayload: any = null;
      let errorListenerCleanup: (() => void) | null = null;

      try {
        const continueLiveStreamDuringAds = this.deps.shouldContinueLiveStreamDuringAds();
        const preloadAd = this.deps.createAdPlayer(continueLiveStreamDuringAds);
        preloadAd.initialize();

        const errorListener = (payload?: any) => {
          hasAdError = true;
          adErrorPayload = payload;
        };
        preloadAd.on("ad_error", errorListener);
        errorListenerCleanup = () => preloadAd.off("ad_error", errorListener);

        await timing.enforceGlobalRateLimit();
        await preloadAd.requestAds(vastUrl);

        preloadAd.pause();

        await new Promise(resolve => setTimeout(resolve, 1500));

        preloadAd.pause();

        if (hasAdError) {
          const isNoFill = adErrorPayload?.isNoFill ||
                          adErrorPayload?.code === 303 ||
                          adErrorPayload?.vastErrorCode === 303;

          if (errorListenerCleanup) {
            errorListenerCleanup();
          }
          preloadAd.destroy();

          timing.recordGamNoFill();

          if (isNoFill) {
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Ad preload returned no-fill: ${vastUrl}`);
            }
            timing.temporaryFailureUrls.set(vastUrl, Date.now());
          } else {
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Ad preload failed with error: ${vastUrl}`, adErrorPayload);
            }
            if (adErrorPayload && !timing.isTemporaryAdError(adErrorPayload as Error)) {
              timing.failedVastUrls.add(vastUrl);
            } else {
              timing.temporaryFailureUrls.set(vastUrl, Date.now());
            }
          }
          return;
        }

        if (!this.poolActive || epoch !== timing.adBreakEpoch) {
          if (this.deps.debug) {
            console.log(
              "[PRELOAD-POOL] Discarding preloaded ad - break ended during fetch"
            );
          }
          if (errorListenerCleanup) {
            errorListenerCleanup();
          }
          try {
            preloadAd.destroy();
          } catch {}
          return;
        }

        const poolEntry: {
          vastUrl: string;
          adController: AdController;
          loadedAt: number;
          isReady: boolean;
          loadPromise: Promise<void>;
          durationSeconds?: number;
        } = {
          vastUrl,
          adController: preloadAd,
          loadedAt: Date.now(),
          isReady: true,
          loadPromise: Promise.resolve(),
        };
        const loadedDuration = preloadAd.getLoadedAdDuration?.();
        if (loadedDuration != null && loadedDuration > 0) {
          poolEntry.durationSeconds = loadedDuration;
        }

        const lateErrorListener = (payload?: any) => {
          const index = this.pool.findIndex(entry => entry.vastUrl === vastUrl);
          if (index >= 0) {
            this.pool.splice(index, 1);
            if (this.deps.debug) {
              console.log(`[PRELOAD-POOL] Late error detected, removed from pool: ${vastUrl}`, payload);
            }
            try {
              preloadAd.destroy();
            } catch {}
          }
        };
        preloadAd.on("ad_error", lateErrorListener);

        this.pool.push(poolEntry);

        if (this.deps.debug) {
          console.log(`[PRELOAD-POOL] Ad preloaded (no errors detected within timeout) (pool size: ${this.pool.length}/${this.maxPoolSize}, duration=${poolEntry.durationSeconds ?? "?"}s)`);
        }

        if (errorListenerCleanup) {
          errorListenerCleanup();
        }
      } catch (error) {
        if (errorListenerCleanup) {
          errorListenerCleanup();
        }

        timing.recordGamNoFill();

        if (this.deps.debug) {
          console.warn(`[PRELOAD-POOL] Failed to preload ad: ${vastUrl}`, error);
        }

        if (error instanceof Error && !timing.isTemporaryAdError(error)) {
          timing.failedVastUrls.add(vastUrl);
        } else if (error instanceof Error && timing.isTemporaryAdError(error)) {
          timing.temporaryFailureUrls.set(vastUrl, Date.now());
        }
      }
    })();

    loadPromise.catch(() => {});
  }

  async preloadPoolLoop(baseVastUrl: string): Promise<void> {
    if (this.loopRunning) {
      return;
    }

    this.loopRunning = true;
    const epoch = this.deps.timing.adBreakEpoch;

    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Starting continuous preload loop");
    }

    while (this.poolActive && epoch === this.deps.timing.adBreakEpoch) {
      if (this.deps.timing.isGamInCooldown()) {
        if (this.deps.debug) {
          console.log("[CIRCUIT-BREAKER] GAM in cooldown, stopping preload pool loop");
        }
        break;
      }

      if (!this.deps.isInAdBreak() && this.pool.length >= this.maxPoolSize) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        continue;
      }

      if (!this.deps.isInAdBreak() && this.deps.getPendingAdBreak() === null) {
        if (this.deps.debug) {
          console.log("[PRELOAD-POOL] Ad break cancelled, stopping preload pool");
        }
        break;
      }

      while (this.pool.length < this.maxPoolSize) {
        if (
          !this.poolActive ||
          epoch !== this.deps.timing.adBreakEpoch ||
          this.deps.timing.isGamInCooldown()
        ) {
          break;
        }

        if (this.deps.isInAdBreak() && !this.deps.timing.hasTimeToStartAnotherAd()) {
          if (this.deps.debug) {
            console.log("[PRELOAD-POOL] Not enough ad break time left, stopping preload");
          }
          break;
        }

        const newUrls = this.deps.generateVastUrlsWithCorrelators(baseVastUrl, 1);
        const newUrl = newUrls[0];

        if (newUrl && !this.deps.timing.failedVastUrls.has(newUrl) && !this.deps.timing.isUrlInCooldown(newUrl)) {
          await this.preloadAdIntoPool(newUrl);
        }

        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    this.loopRunning = false;

    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Preload loop ended");
    }
  }

  getPreloadedAd(): { vastUrl: string; adController: AdController } | null {
    if (this.pool.length === 0) {
      return null;
    }

    const entry = this.pool.shift();
    if (!entry || !entry.isReady) {
      return null;
    }

    if (this.deps.debug) {
      console.log(`[PRELOAD-POOL] Using preloaded ad from pool (${this.pool.length} remaining)`);
    }

    return {
      vastUrl: entry.vastUrl,
      adController: entry.adController,
    };
  }

  stop(): void {
    this.poolActive = false;
    this.loopRunning = false;

    for (const entry of this.pool) {
      try {
        entry.adController.destroy();
      } catch (error) {
        if (this.deps.debug) {
          console.warn("[PRELOAD-POOL] Error destroying preloaded controller:", error);
        }
      }
    }

    this.pool = [];

    if (this.deps.debug) {
      console.log("[PRELOAD-POOL] Preload pool stopped and cleaned up");
    }
  }
}
